import React from 'react';
import PropTypes from 'prop-types';
import { formatDateLong } from '../../utils/dateUtils';
import {
  Box,
  Paper,
  Grid,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert
} from '@mui/material';
import StatusBadge from '../orders/StatusBadge';
import {
  Search as SearchIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon
} from '@mui/icons-material';

function StationeryOrdersTabContent({
  stationeryOrders,
  loading,
  error,
  searchQuery,
  setSearchQuery,
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
  statusFilter,
  setStatusFilter,
  getProcessedOrders,
  getUniqueStatuses,
  getTotalPrice,
  ActionsMenu
}) {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ width: '100%', mb: 2 }}>
        {/* Filters */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Grid container spacing={2} alignItems="center">
            {/* Search */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by organization or ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Sort By */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="date">Date</MenuItem>
                  <MenuItem value="organization">Organisation</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Sort Order */}
            <Grid item xs={12} sm={6} md={2}>
              <ToggleButtonGroup
                value={sortOrder}
                exclusive
                onChange={(e, newOrder) => {
                  if (newOrder !== null) {
                    setSortOrder(newOrder);
                  }
                }}
                size="small"
                fullWidth
              >
                <ToggleButton value="asc" aria-label="ascending">
                  <ArrowUpwardIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="desc" aria-label="descending">
                  <ArrowDownwardIcon fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>

            {/* Status Filter */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter by Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Filter by Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  {getUniqueStatuses(stationeryOrders).map((status) => (
                    <MenuItem key={status} value={status.toLowerCase()}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        {/* Stationery Orders Table */}
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="stationery orders table">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Organisation</TableCell>
                <TableCell>Product Type</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Total Price</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getProcessedOrders(stationeryOrders)
                .map((order) => (
                  <TableRow
                    key={order.id}
                    hover
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell>{order.createdAt ? formatDateLong(order.createdAt) : 'N/A'}</TableCell>
                    <TableCell component="th" scope="row">
                      {order.id}
                    </TableCell>
                    <TableCell>{order.organizationName || order.businessName || order.customerName || 'N/A'}</TableCell>
                    <TableCell>{order.productType || order.product || 'N/A'}</TableCell>
                    <TableCell>{order.quantity || 'N/A'}</TableCell>
                    <TableCell>${getTotalPrice(order)}</TableCell>
                    <TableCell>
                      <StatusBadge status={order.status || 'Pending'} />
                    </TableCell>
                    <TableCell>
                      <ActionsMenu order={order} />
                    </TableCell>
                  </TableRow>
                ))}
              {getProcessedOrders(stationeryOrders).length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No stationery orders found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
}

StationeryOrdersTabContent.propTypes = {
  stationeryOrders: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  searchQuery: PropTypes.string.isRequired,
  setSearchQuery: PropTypes.func.isRequired,
  sortBy: PropTypes.string.isRequired,
  setSortBy: PropTypes.func.isRequired,
  sortOrder: PropTypes.string.isRequired,
  setSortOrder: PropTypes.func.isRequired,
  statusFilter: PropTypes.string.isRequired,
  setStatusFilter: PropTypes.func.isRequired,
  getProcessedOrders: PropTypes.func.isRequired,
  getUniqueStatuses: PropTypes.func.isRequired,
  getTotalPrice: PropTypes.func.isRequired,
  ActionsMenu: PropTypes.elementType.isRequired
};

export default StationeryOrdersTabContent;
